import moment from "moment";

/**
 * Helpers for holiday_setting_detail rows.
 * Convention (matches existing data): *_day_off times = OFF hours
 * (full day = morning + afternoon pairs filled), *_day_offset times = make-up WORKING hours.
 */

export const HOLIDAY_TYPE = { HOLIDAY_LEAVE: 1, SPECIAL_LEAVE: 2 };
export const SESSION = { FULL: "full", MORNING: "morning", AFTERNOON: "afternoon" };

const OFF_FIELDS = {
  morning: ["start_morning_day_off", "end_morning_day_off"],
  afternoon: ["start_afternoon_day_off", "end_afternoon_day_off"]
};
const OFFSET_FIELDS = {
  morning: ["start_morning_day_offset", "end_morning_day_offset"],
  afternoon: ["start_afternoon_day_offset", "end_afternoon_day_offset"]
};
const ALL_TIME_FIELDS = [
  ...OFF_FIELDS.morning,
  ...OFF_FIELDS.afternoon,
  ...OFFSET_FIELDS.morning,
  ...OFFSET_FIELDS.afternoon
];

// "08:00:00" -> "08:00"
const toHm = time => (time ? String(time).slice(0, 5) : "");

export function newRow() {
  const row = {
    day_off: "",
    type: HOLIDAY_TYPE.HOLIDAY_LEAVE,
    is_compensation: false,
    session: SESSION.FULL,
    day_offset: ""
  };
  ALL_TIME_FIELDS.forEach(field => (row[field] = ""));
  return row;
}

// Session is UI-only: derived from which off pair has values
function detectSession(detail) {
  const hasMorning = !!detail.start_morning_day_off;
  const hasAfternoon = !!detail.start_afternoon_day_off;
  if (hasMorning && !hasAfternoon) return SESSION.MORNING;
  if (!hasMorning && hasAfternoon) return SESSION.AFTERNOON;
  return SESSION.FULL;
}

export function fromApi(detail) {
  const row = {
    day_off: detail.day_off || "",
    type: Number(detail.type) || HOLIDAY_TYPE.HOLIDAY_LEAVE,
    is_compensation: Number(detail.is_compensation) === 1,
    session: detectSession(detail),
    day_offset: detail.day_offset || ""
  };
  ALL_TIME_FIELDS.forEach(field => (row[field] = toHm(detail[field])));
  return row;
}

export function toPayload(row) {
  const payload = {
    day_off: row.day_off,
    type: row.type,
    is_compensation: row.is_compensation ? 1 : 0,
    day_offset: row.day_offset || null
  };
  ALL_TIME_FIELDS.forEach(field => (payload[field] = row[field] || null));
  return payload;
}

export const isSessionVisible = (session, half) =>
  session === SESSION.FULL || session === half;

// Work time of the date's weekday; weekend/unknown falls back to Monday or first configured day
function workTimeOf(date, workTimes) {
  const dayName = moment(date, "YYYY-MM-DD").locale("en").format("dddd");
  return workTimes[dayName] || workTimes.Monday || Object.values(workTimes)[0] || null;
}

function fillPair(row, fields, start, end) {
  if (!row[fields[0]] && !row[fields[1]]) {
    row[fields[0]] = toHm(start);
    row[fields[1]] = toHm(end);
  }
}

/**
 * Prefill empty off pairs matching the selected session and clear hidden pairs.
 * Never overwrites hours already entered.
 */
export function applyOffTimes(row, workTimes) {
  ["morning", "afternoon"].forEach(half => {
    const fields = OFF_FIELDS[half];
    if (!isSessionVisible(row.session, half)) {
      fields.forEach(field => (row[field] = ""));
      return;
    }
    const workTime = row.day_off ? workTimeOf(row.day_off, workTimes) : null;
    if (workTime) {
      fillPair(row, fields, workTime[`start_time_${half}`], workTime[`end_time_${half}`]);
    }
  });
}

// Prefill empty make-up pairs from work time; clear them when no make-up day
export function applyOffsetTimes(row, workTimes) {
  ["morning", "afternoon"].forEach(half => {
    const fields = OFFSET_FIELDS[half];
    if (!row.day_offset) {
      fields.forEach(field => (row[field] = ""));
      return;
    }
    const workTime = workTimeOf(row.day_offset, workTimes);
    if (workTime) {
      fillPair(row, fields, workTime[`start_time_${half}`], workTime[`end_time_${half}`]);
    }
  });
}
